<template>
	<header-block :title="voucher ? voucher.discount.merchant.name : 'Voucher'"></header-block>
	<div class="topPanel" v-if="customer && voucher">
		<VoucherCarousel 
			:items="selectedVouchers" 
			:formatter="formatter"
			:paginationDots="true"
			:visibleDots="5"
			:defaultVoucherId="voucher ? voucher.id : null"
			@voucherSelected="selectedVoucher = $event" 
			/>
			

		<v-row class="button-row" v-if="voucher.currentBalance > 0">
			<v-col class="button-col">
				<MGButton text="Spend voucher" :stacked="true" :fullWidth="true" @click="this.openDrawer();"/>
				<MGButton text="View voucher" :stacked="true" :textOnly="true" :fullWidth="true" @click="viewVoucher();"/>
			</v-col>
		</v-row>
		<v-row class="button-row" v-if="voucher.currentBalance == 0">
			<v-col class="button-col">
				<MGButton 
					v-if="voucher.currentBalance == 0" 
					text="View voucher" 
					:stacked="true"
					:noCaps="true"
					:fullWidth="true" 
					@click="viewVoucher()"/>
				<MGButton 
					v-if="voucher.currentBalance == 0" 
					text="Buy another voucher" 
					:noCaps="true"
					:stacked="true"
					:fullWidth="true" 
					@click="buyVoucher()"/>
			</v-col>
		</v-row>
	</div>
	<div v-else>
		<v-skeleton-loader type="image, heading" class="topPanel" />
	</div>

	<div class="transactionHistoryInfo" v-if="customer && voucher">
		<v-row justify="center" class="constrained-row">
			<v-col class="text-center">
				<h2>Transaction History <img src="@/assets/i.png" class="info-icon" @click="this.$router.push('/TrackYourSpend')" /></h2>
			</v-col>
		</v-row>
		<v-row class="mt-4 constrained-row">
			<v-col class="note">
				<span>Keep track of your spending to make every penny count.</span>
			</v-col>
		</v-row>
	</div>
	<transition>
		<div class="transactionHistory"  v-if="customer && voucher">
			<v-row class="mt-0">
				<v-col class="right">Balance: {{ formatter.format(voucher.currentBalance)}}</v-col>
			</v-row>

			<VoucherTransactionsList 
				:key="selectedVouchersKey" 
				@transactionDeleted="transactionDeleted" 
				@transactionUpdated="transactionUpdated" 
				search="false" 
				:localVoucher="voucher" 
				:formatter="formatter" 
				:today="today" 
				:yesterday="yesterday" 
				/>
		</div>
	</transition>
	
	<div v-if="voucher">
		<v-row class="button-row mt-5" v-if="voucher.archived !== true">
			<v-col class="button-col">
				<MGButton 
					text="Archive Voucher" 
					:fullWidth="true" 
					:outline="true"
					:disabled="archiveClicked"
					@click="archiveVoucher()"/>
			</v-col>
		</v-row>
		<v-row class="button-row mt-5" v-if="voucher.archived == true && voucher.currentBalance > 0">
			<v-col class="button-col">
				<MGButton 
					text="Unarchive Voucher" 
					:fullWidth="true" 
					:outline="true"
					:disabled="archiveClicked"
					@click="unarchiveVoucher()"/>
			</v-col>
		</v-row>
	</div>

	<DrawerPanel v-if="guide" ref="guideDrawer" openHeight="88%" :closedHeight="_closedDrawerHeightNoMenu()" backgroundColor="white" :bottom="_peekingDrawerBottom_NoMenu()" title="How To Use Guide" 		openBackgroundColor="white">
		<iframe :src="'/#/articles/' + guide.id" style="border: 0px; width: 100%; height: 100%"></iframe>
	</DrawerPanel>

	<DrawerPanel v-if="voucher" 
			ref="drawer" 
			:back="true"
			:close="false"
			openHeight="88%" 
			closedHeight="0px" 
			bottom="-18px" 
			backgroundColor="white" 
			openBackgroundColor="white" 
			:blurBackground="true"
			:closeBackground="false"
			@goBack="closeDrawer()"
			:keyboard="isKeyboardVisible"
			title="Spend voucher">
		<v-row v-if="voucher.currentBalance > 0">
			<v-col class="purchaseLabel"><b>Track your spend</b><br/><span class="purchaseText">Enter the amount you'll be spending.</span></v-col>
			<v-col class="right" cols="2"><img class="cardLogo" :src="voucher.discount.cardUrl"/></v-col>
		</v-row>
		<v-row>
			<v-col>
				<v-text-field v-if="voucher.currentBalance > 0"
						ref="purchaseInput"
						variant="solo"
						:flat="true"
						clearable
						single-line
						rounded
						outlined
						prefix="£" 
						v-model="purchaseTotal" 
						type="text" 
						inputmode="decimal"
						@input="validatePurchaseAmount"
						@keydown="_preventNegative($event)"
						:placeholder="voucher.currentBalance"
						label="Enter amount" />
			</v-col>
		</v-row>
		<v-row v-if="voucher.currentBalance > 0" class="remaining-row">
			<v-col>
				<span class="remaining clickable" v-if="!spendErrorText" @click="fillFullAmount">
					Remaining: <b>{{ formatter.format(calcRemaining()) }}</b>
				</span>
				<span class="remaining" v-else>
					Please enter an amount no more than {{ formatter.format(voucher.currentBalance) }}.
				</span>
			</v-col>
		</v-row>
		<v-row class="spendbutton">
			<v-col>
				<MGButton
					v-if="voucher.currentBalance > 0"
					:text="spendButtonText"
					@click="addVoucherTransaction(purchaseTotal)"
					:flat="true"
					:fullWidth="true"
					:disabled="spendingInProgress"
				/>
				<MGButton v-if="!voucher.currentBalance > 0" :fullWidth="true" text="View" @click="viewVoucher()" :flat="true"/>
			</v-col>
		</v-row>
	</DrawerPanel>

	<MGDialog
		v-model="showArchiveConfirmationModal" 
		max-width="500px" 
		:persistent="true"
		content-height="100px"
	>
		<template #content>
			<p>Are you sure you want to archive this voucher?</p>
		</template>

		<template #actions>
			<MGButton :fullWidth="true" text="Confirm" @click="confirmArchiveVoucher" />
			<MGButton :stacked="true" :fullWidth="true" :outline="true" text="Cancel" @click="cancelArchiveVoucher" />
		</template>
	</MGDialog>

	<MGDialog 
		v-model="showRedemptionModal" 
		max-width="500px" 
		:persistent="true"
		title="Confirmation"
		:icon="cardUrl"
		content-height="180px"
	>
		<template #content>
			<h2 class="confirmation-header">Track your spend</h2>
			<p class="confirmation-text">Confirm the amount you spent.</p>
			
			<v-text-field
				v-model="actualAmount"
				label="Enter amount"
				type="text" 
				inputmode="decimal"
				prefix="£"
				variant="solo"
				:flat="true"
				clearable
				single-line
				outlined
				rounded
				@keydown="_preventNegative($event)"
				@input="validateActualAmount"
				autofocus
				@keyup.enter="confirmActualAmount"
			></v-text-field>
			<v-alert class="confirmAlert" density="compact" type="info" :icon="false" variant="plain" v-if="confirmationErrorText" :text="confirmationErrorText" />
		</template>

		<template #actions>
			<MGButton :noCaps="true" :stacked="true" :fullWidth="true" class="button-row" text="Save" @click="confirmPreviousRedemption" :disabled="!previousRedemption" />
			<MGButton :noCaps="true" :stacked="true" :fullWidth="true" class="button-row" :outline="true" text="I didn't spend anything" @click="removeTransaction" :disabled="!previousRedemption" />
		</template>

		<template #footer>
			<div class="ease-of-use-box">
				<p class="text-caption mb-2">How easy was it to use this voucher at {{ voucher.discount.merchant.name }}? (Optional)</p>
				<div class="star-rating">
					<v-icon 
						v-for="star in 5" 
						:key="star"
						:style="star <= easeOfUseRating ? 'background: #413655; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;' : 'color: #cccccc;'"
						@click="easeOfUseRating = star"
						class="star-icon"
						size="24"
					>
						mdi-star
					</v-icon>
				</div>
				<div class="text-caption mt-1">
					{{ easeOfUseRating > 0 ? `${easeOfUseRating} out of 5 stars` : '' }}
				</div>
			</div>
		</template>

	</MGDialog>

</template>

<script>
import Services from '../mixins/Services';
import Helpers from '../mixins/Helpers';
import HeaderBlock from './elements/HeaderBlock.vue';
import DrawerPanel from './elements/DrawerPanel.vue';
import VoucherTransactionsList from './elements/VoucherTransactionsList.vue';
import MGButton from './elements/MGButton.vue';
import MGDialog from './elements/MGDialog.vue';
import VoucherCarousel from './VoucherCarousel.vue';

export default {
	mixins: [Services, Helpers],
	components: { HeaderBlock, DrawerPanel, VoucherTransactionsList, MGButton, MGDialog, VoucherCarousel },
	data() {
		return {
			purchaseTotal: null,
			spendErrorText: null,
			confirmationErrorText: null,
			isDrawerOpen: false,
			isKeyboardVisible: false,
			showRedemptionModal: false,
			showArchiveConfirmationModal: false,
			previousRedemption: null,
			actualAmount: null,
			actualAmountError: null,
			easeOfUseRating: 0,
			easeOfUseSubmitted: false,
			guide: null,
			currentDialogPage: 'confirmation',
			selectedVouchers: [],
			selectedVoucher: 0,
			selectedVouchersKey: 0,
			archiveClicked: false,
			fromArchived: false,
			fromWallet: false,
			cardUrl: null,
			spendingInProgress: false
			// rules: [
			// 	value => !!value || 'Please enter an amount',
			// 	value => (value && value <= this.voucher.currentBalance) || `Amount must be less than or equal to ${this.formatter.format(this.voucher.currentBalance)}`,
			// 	value => (value && value > 0) || 'Amount must be positive'
			// ]
		}
	},
	watch: {
		selectedVoucher(newIndex) {
			this.voucher = this.selectedVouchers[newIndex];
			this.checkPreviousRedemption(this.voucher);
		},
		voucher: {
			handler(newVoucher) {
				if (newVoucher && newVoucher.customerVoucherTransactions) {
					newVoucher.customerVoucherTransactions.sort((a, b) => new Date(b.createdDateTime) - new Date(a.createdDateTime));
				}
			},
			immediate: true
		}
	},
	methods: {
		calcRemaining: function() {
			if (this.purchaseTotal === null || this.purchaseTotal === undefined || this.purchaseTotal === '') {
				return this.voucher.currentBalance;
			}
			const remaining = this.voucher.currentBalance - this.purchaseTotal;
			return remaining >= 0 ? remaining : 0;
		},
		fillFullAmount: function() {
			this.purchaseTotal = this.voucher.currentBalance;
			this.validatePurchaseAmount();
		},
		validatePurchaseAmount: function() {
			if (this.purchaseTotal === '.') {
				this.purchaseTotal = 0;
			}
			if (this.purchaseTotal === '') {
				this.purchaseTotal = null;
			}
			if (this.purchaseTotal === null || this.purchaseTotal === undefined) {
				this.spendErrorText = null;
				return;
			}
			this.spendErrorText = null;
			const purchaseAmount = Number(this.purchaseTotal);
			if (purchaseAmount > this.voucher.currentBalance) {
				this.spendErrorText = `Please enter an amount no more than ${this.formatter.format(this.voucher.currentBalance)}`;
			} else if (purchaseAmount < 0) {
				this.purchaseTotal = 0;
				this.spendErrorText = 'Please enter a positive amount';
			}
		},
		validateActualAmount: function() {
			if (this.actualAmount === null || this.actualAmount === undefined || this.actualAmount === '') {
				this.confirmationErrorText = null;
				return;
			}
			this.confirmationErrorText = null;
			var maxAmount = Number(this.previousRedemption.newCurrentBalance) + Number(this.previousRedemption.amount);
			if (this.actualAmount > maxAmount) {
				this.confirmationErrorText = `Must be no more than ${this.formatter.format(maxAmount)}`;
				this.actualAmount = maxAmount;
			} else if (this.actualAmount <= 0) {
				this.actualAmount = 0;
				this.confirmationErrorText = 'Please enter a positive amount';
			}
		},
		fetchVouchers(callback) {
			console.log('Fetching vouchers for merchant', this.$route.params.id);
			if (this.fromArchived) {
				this._getCustomerVoucher(this.$route.params.id, () => {
					this.selectedVouchers = [this.voucher];
					this.handleVoucherData(callback);
				});
			} else if (this.fromWallet) {
				this._getCustomerVoucher(this.$route.params.id, () => {
					var voucher = this.voucher;
					var merchantId = voucher.discount.merchant.id;
					this._getCustomerVouchers(() => {
						console.log(this.$route.params.id);
						this.selectedVouchers = this.vouchers.vouchers;
						this.voucher = this.selectedVouchers.find(v => v.id == this.$route.params.id);
						this.handleVoucherData(callback);
					}, merchantId, false);
				});
			} else { // from aiop - by merchant (merchantid passed in)
				console.log('Fetching vouchers for merchant normally with merchant ID:', this.$route.params.id);
				this._getCustomerVouchers(() => {
					console.log('Vouchers fetched:', this.vouchers);
					this.selectedVouchers = this.vouchers.vouchers;
					this.voucher = this.selectedVouchers[0];
					console.log('Selected vouchers:', this.selectedVouchers);
					this.handleVoucherData(callback);
				}, this.$route.params.id, /* archived */ this.fromArchived, true) || [];
			}
		},

		handleVoucherData(callback) {
			// Check for previous redemption after voucher loads
			this.selectedVouchers.forEach(voucher => {
				this.checkPreviousRedemption(voucher);
			});
			
			const merchantName = this.selectedVouchers[0]?.discount?.merchant?.name?.replaceAll("&", '')?.toUpperCase();
			if (merchantName) {
				this._getCmsArticleByKey(`VG_${merchantName.substring(0, 10)}`, (article) => {
					if (article) {
						this.guide = article;
					}
				});
			}
			// this.voucher = this.selectedVouchers[this.selectedVoucher];
			if (callback) callback();
		},

		getSelectedVouchers() {
			console.log('Getting selected vouchers');
			this.fetchVouchers();
		},

		transactionDeleted() {
			this._clearCustomerVouchers();
			this.getSelectedVouchers();
		},
		transactionUpdated() {
			this._clearCustomerVouchers();
			this.getSelectedVouchers();
		},
		onLoad: async function() {
			this.fromArchived = this.$route.query.archive === 'true';
			this.fromWallet = this.$route.query.fromWallet === 'true';
			this._getCustomer(() => {
				this.getSelectedVouchers();
			});
		},
		openDrawer: function() {
			this.isDrawerOpen = true;
			this.spendingInProgress = false;
			this.purchaseTotal = null;
			this.actualAmount = this.previousRedemption?.amount || 0;
			this.$refs.drawer.toggleDrawer();
			setTimeout(() => {
				this.isKeyboardVisible = true;
				if (this.$refs.purchaseInput && this.$refs.purchaseInput.focus) {
					this.$refs.purchaseInput.focus();
				}
			}, 300); 
		},
		closeDrawer: function() {
			this.isDrawerOpen = false;
			this.isKeyboardVisible = false;
			this.$refs.drawer.toggleDrawer();
		},
		addVoucherTransaction: function(amount) {
			if (amount === null) {
				amount = this.voucher.currentBalance;
			}
			if (this.purchaseTotal > this.voucher.currentBalance) {
				this.errorText = "Invalid purchase total";
				return;
			}
			this.spendingInProgress = true;
			this._addVoucherTransaction(this.voucher.id, amount, (transaction) => {
				// Save redemption to local storage after transaction is created
				this.saveRedemptionToStorage(amount, transaction);

				if (this.isDrawerOpen) {
					this.closeDrawer();
				}
				this._clearCustomerVouchers();
				this.getSelectedVouchers(() => {
					this.selectedVouchersKey++;
				});
				window.location = this.voucher.url;
			});
		},
		saveRedemptionToStorage: function(amount, transaction) {
			const redemption = {
				merchantId: this.voucher.discount.merchant.id,
				merchantName: this.voucher.discount.merchant.name,
				amount: amount,
				date: new Date().toISOString(),
				voucherId: this.voucher.id,
				transactionId: transaction ? transaction.id : null, 
				confirmed: false,
				easeOfUseRating: this.easeOfUseRating,
				newCurrentBalance: this.voucher.currentBalance - amount,
				cardUrl: this.voucher.discount.cardUrl
			};

			const existingRedemptions = JSON.parse(localStorage.getItem('voucherRedemptions') || '[]');
			existingRedemptions.push(redemption);
			localStorage.setItem('voucherRedemptions', JSON.stringify(existingRedemptions));
		},
		checkPreviousRedemption: function(voucher) {
			if (!voucher || !voucher.discount.merchant) return;

			const merchantId = voucher.discount.merchant.id;
			const existingRedemptions = JSON.parse(localStorage.getItem('voucherRedemptions') || '[]');
			
			const merchantRedemptions = existingRedemptions
				.filter(r => r.merchantId === merchantId && !r.confirmed)
				.sort((a, b) => new Date(b.date) - new Date(a.date));
			
			if (merchantRedemptions.length > 0) {
				const mostRecent = merchantRedemptions[0];
				
				if (!mostRecent.amount || !mostRecent.date || !mostRecent.merchantName) {
					console.warn('Invalid redemption data found:', mostRecent);
					return;
				}
				
				const redemptionDate = new Date(mostRecent.date);
				const now = new Date();
				const daysDifference = Math.floor((now - redemptionDate) / (1000 * 60 * 60 * 24));
				
				if (daysDifference <= 7) {
					this.previousRedemption = mostRecent;
					this.currentDialogPage = 'confirmation';
					this.actualAmount = mostRecent.amount;
					this.cardUrl = mostRecent.cardUrl;
					this.showRedemptionModal = true;
				}
			}
		},
		confirmPreviousRedemption: function() {
			if (this.previousRedemption.amount != this.actualAmount) {
				this.confirmActualAmount();
			}
			if (this.previousRedemption) {
				this.deleteRedemptionFromStorage(this.previousRedemption);
				
				if (this.easeOfUseRating > 0 && !this.easeOfUseSubmitted) {
					const easeOfUseData = {
						id: this.voucher.id,
						score: this.easeOfUseRating
					};
					this._addVoucherEaseOfUse(easeOfUseData, () => {
						console.log('Ease of use review added:', this.easeOfUseRating);
					});
					this.easeOfUseSubmitted = true;
				}
			}
			this.showRedemptionModal = false;
			this.previousRedemption = null;
			this.easeOfUseRating = 0;
			this.easeOfUseSubmitted = false;
			this.currentDialogPage = 'confirmation';
		},
		formatDate: function(dateString) {
			const date = new Date(dateString);
			return date.toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'long',
				year: 'numeric'
			});
		},
		viewVoucher: function() {
			if (this.isDrawerOpen){
				this.closeDrawer();
			}
			window.location = this.voucher.url;
		},
		archiveVoucher: function() {
			this.showArchiveConfirmationModal = true;
	
		},
		confirmArchiveVoucher: function() {
			this.showArchiveConfirmationModal = false;
			this.archiveClicked = true;
			this._archiveVoucher(this.voucher.id, () => {
				this._clearCustomerVouchers();
				if (this.fromWallet) {
					this.$router.push({ path: '/Wallet', query: { poll: 'true' } });
					return;
				}
				this.$router.push('/MyVouchers/Merchants');
			});
		},
		unarchiveVoucher: function() {
			this.archiveClicked = true;
			this._unarchiveVoucher(this.voucher.id, () => {
				this._clearCustomerVouchers();
				this.$router.push('/MyVouchers/Merchants');
			});
		},
		cancelArchiveVoucher: function() {
			this.showArchiveConfirmationModal = false;
		},
		buyVoucher: function() {
			this.$router.replace({ path: '/vouchers/amount/' + this.voucher.discount.id });
		},
		deleteRedemptionFromStorage: function(redemption) {
			const existingRedemptions = JSON.parse(localStorage.getItem('voucherRedemptions') || '[]');
			
			const updatedRedemptions = existingRedemptions.filter(r => {
				return !(r.merchantId === redemption.merchantId && 
						r.amount === redemption.amount && 
						r.date === redemption.date &&
						r.voucherId === redemption.voucherId);
			});
			
			localStorage.setItem('voucherRedemptions', JSON.stringify(updatedRedemptions));
		},
		updateRedemptionAmount: function(redemption) {
			const existingRedemptions = JSON.parse(localStorage.getItem('voucherRedemptions') || '[]');
			
			const updatedRedemptions = existingRedemptions.filter(r => {
				return !(r.merchantId === redemption.merchantId && 
					r.amount === redemption.amount && 
					r.date === redemption.date &&
					r.voucherId === redemption.voucherId &&
					r.transactionId === redemption.transactionId);
			});
			localStorage.setItem('voucherRedemptions', JSON.stringify(updatedRedemptions));
		},
		confirmActualAmount: function() {
			if (!this.actualAmount || this.actualAmount <= 0) {
				return;
			}

			const newAmount = parseFloat(this.actualAmount);
			const redemption = this.previousRedemption;

			const currentBalance = parseFloat(redemption.newCurrentBalance);
			const originalAmount = parseFloat(redemption.amount);
			
			if (currentBalance + originalAmount - newAmount < 0) {
				this.actualAmountError = 'The actual amount cannot exceed the current balance.';
				return;
			}

			// Clear any existing error
			this.actualAmountError = null;

			if (redemption) {
				this.updateRedemptionAmount(redemption);
				if (redemption.transactionId) {
					this._updateVoucherTransaction(redemption.voucherId, redemption.transactionId, newAmount, () => {
						this._clearCustomerVouchers();
						this.getSelectedVouchers();
					});
				} else {
					console.error('No transaction ID found in redemption:', redemption);
				}
			}

			this.showRedemptionModal = false;
			this.actualAmount = '';
			this.actualAmountError = null;
			this.previousRedemption = null;
			this.easeOfUseRating = 0;
			this.easeOfUseSubmitted = false;
			this.currentDialogPage = 'confirmation';
		},
		goToAdjustmentPage: function() {
			if (this.previousRedemption) {
				// Add ease of use review if rating was provided and not already submitted
				if (this.easeOfUseRating > 0 && !this.easeOfUseSubmitted) {
					const easeOfUseData = {
						id: this.voucher.id,
						score: this.easeOfUseRating
					};
					this._addVoucherEaseOfUse(easeOfUseData, () => {
						console.log('Ease of use review added:', this.easeOfUseRating);
					});
					this.easeOfUseSubmitted = true;
				}
				
				// Navigate to the adjustment page
				this.actualAmount = this.previousRedemption.amount.toString();
				this.actualAmountError = null;
				this.currentDialogPage = 'adjustment';
			}
		},
		goToConfirmationPage: function() {
			this.actualAmountError = null;
			this.currentDialogPage = 'confirmation';
		},
		removeTransaction: function() {
			if (this.previousRedemption && this.previousRedemption.transactionId) {
				this.deleteRedemptionFromStorage(this.previousRedemption);
				this._deleteVoucherTransaction(this.previousRedemption.voucherId, this.previousRedemption.transactionId, () => {
					this._clearCustomerVouchers();
					this.getSelectedVouchers();
				});
			}
			this.showRedemptionModal = false;
			this.actualAmount = '';
			this.previousRedemption = null;
			this.easeOfUseRating = 0;
			this.easeOfUseSubmitted = false;
		},
	},
	mounted: function () {
		this.onLoad();
	},
	computed: {
		spendButtonText() {
			console.log('Computing spendButtonText with purchaseTotal:', this.purchaseTotal, 'and voucher currentBalance:', this.voucher.currentBalance);
			const amount = this.purchaseTotal !== null ? this.purchaseTotal : this.voucher.currentBalance;
			return `Spend ${this.formatter.format(amount)}`;
		},
		totalMerchantAmount() {
			if (!this.merchantVouchers || this.merchantVouchers.length === 0) {
				return 0;
			}
			return this.merchantVouchers.reduce((total, voucher) => {
				return total + (voucher.faceValue || 0);
			}, 0);
		}
	},
}
</script>

<style scoped>
.spendbutton {
	margin-top: 10px;
}
.alert {
	margin-top: 12px;
}
.confirmAlert {
	margin-top: -25px;
	font-size: 12px;
}
.confirmation-header {
	font-size:15px;
}
.confirmation-text {
	margin-top:-10px;
	font-size:13px;
}
.text-caption {
	font-size:13px;
}
.continue-shopping-button {
	text-align: center;
	font-size: 15px;
	font-weight: bold;
	margin-top: 12px;
}

.info-icon {
    width: 20px;
    height: 20px;
    margin-left: 4px;
    position: relative;
    top: 6px;
}
.title {
	font-size: 14px;
	font-weight: bold;
}
.purchaseLabel {
	padding-top: 17px !important;
	font-size: 15px !important;
}
.purchaseText {
	font-size: 13px !important;
}
.purchaseTotal {
	padding-top: 0px !important;
}
.info-header {
	background-color: #ffffff44;
	border-radius: 16px;
	margin:30px;
	padding: 10px;
	margin-top: 0px;
}

.button-row {
	padding:0px;
}
.button-col {
	padding-top:0px;
}

.balance-col {
	padding-left: 20px;
	font-size: 16px;
	text-align: left;
}

.balance-title {
	font-size: 12px !important;
	margin-bottom: 0px;
	font-weight: lighter;
}

.divider {
	width: 48px;
	margin-bottom: 8px;
	margin-top: 8px;
	margin-right: auto;
	margin-left: auto;
	background: var(--Keyline, rgba(51, 40, 71, 0.10));
	height: 4px;
	border-radius: 4px;
}

.drawer {
	border-radius: 16px 16px 0px 0px;
	border: 0px;
	overflow-y: hidden;
	background-color: var(--background-color);
	position: fixed;
	width: 100%;
	left: 0px;
	transition: all .3s ease-out;
}

.topTabButton
{
	margin-top: 0px;
}

.topMenuCol {
	padding-left: 5px;
	padding-right: 5px;
}

.topMenuRow
{
	margin-left: -5px;
	margin-right: -5px;
	margin-bottom: 15px;
}

.balanceHeader {
	background: linear-gradient(202deg, #FCE5D4 49.85%, #E0C5C8 100%);
	clear:both;
	margin-left: -30px;
	margin-right: -30px;
	margin-top: -138px;
	margin-bottom: -80px;
	color: var(--text-color);
	font-size: 15px;
	text-align: center;
	padding-top: 15vh;
	padding-bottom: 75px;
	p{
		font-size: 48px;
		font-family: 'Gotham-Heavy';
		margin-top: 0px;
	};
	.small {
		font-size: 10px;
		margin-top: -40px;
	}
}
.button-row {
	padding:0px;
}
.button-col {
	padding-top:0px;
}
.cardLogo
{
	width: 40px;
	border-radius: 4px;
}
.topPanel {
	padding: 20px; 
	padding-top: 140px; 
	margin-left: -30px; 
	margin-right: -30px; 
	margin-top: -138px;
	background: var(--background-color-alt);
}
.transactionHistoryInfo {
	margin-left: -30px;
	margin-right: -30px;
	padding-bottom: 10px;
	background-color: #F3F1ED;
}
.transactionHistory {
	padding-bottom: 20px;
	margin-bottom: 20px;
	max-height: 250px;
}

.constrained-row {
	margin-left: 75px;
	margin-right: 75px;
}
.v-text-field ::v-deep(.v-field) {
	border: 1px solid #413655 !important;
	border-radius: 5px !important;
}
.remaining-row {
	font-size: 13px;
	margin-top: -25px;
}
.remaining {
	background-color: #F9F7F3;
	padding: 9px;
	border-radius: 3px;
}

.remaining.clickable {
	transition: background-color 0.2s ease;
}

.remaining.clickable:active {
	background-color: #E5DFD1;
}

/* Star rating styles */
.star-rating {
	display: flex;
	gap: 4px;
}

.star-icon {
	cursor: pointer;
	transition: transform 0.2s ease;
}

.star-icon:hover {
	transform: scale(1.2);
}

.ease-of-use-box {
	background-color: #f5f5f5;
	border-radius: 0 0 12px 12px;
	padding: 16px;
	border: 1px solid #e0e0e0;
}

.error-message {
	color: #d32f2f;
	font-size: 14px;
	padding: 8px 12px;
	background-color: #ffebee;
	border-radius: 4px;
	border-left: 4px solid #d32f2f;
}

.note {
	text-align: center;
	margin-top: -20px;
	margin-bottom: 10px;
}

.vouchers-carousel {
  background: transparent;
}

.vouchers-carousel ::v-deep(.v-carousel__controls) {
  display: none;
}

.vouchers-carousel ::v-deep(.v-carousel__progress) {
  display: none;
}

.vouchers-carousel ::v-deep(.v-carousel__delimiter) {
  display: none;
}
</style>